import * as accountPage from './pages/account'
import * as appsPage from './pages/apps'
import * as authPage from './pages/auth'
import * as billingPage from './pages/billing'
import * as customPage from './pages/custom'
import * as integratePage from './pages/integrate'
import * as libraryPage from './pages/library'
import * as notificationsPage from './pages/notifications'
import * as supportPage from './pages/support'
import * as usagePage from './pages/usage'
import * as utilityPage from './pages/utility'

export default {
  vendorInvite: {
    title: 'You’re invited to Spatius Studio',
    description: 'Create a new Studio account managed by {vendor}.',
    disclosure: '{vendor} will have read-only access to your account ID, display name and email address; your custom avatar names, IDs and thumbnails; and your billing plan, subscription status, maximum concurrent sessions and maximum session duration. They cannot access avatar assets or change your account.',
    existing: 'Already have a Studio account? Contact Spatius to connect it to your partner.',
    signedIn: 'You’re signed in as {email}. Invitations create new accounts; sign out to continue.',
    signOut: 'Sign out to continue',
    continue: 'Continue to create account',
    normalLogin: 'Sign in without this invitation',
    recoveryTitle: 'Reopen your invitation to continue',
    recoveryDescription: 'Open the original invitation link again to create your partner-managed account. To leave this invitation, explicitly choose to sign in without it.',
    finishSetup: 'Finish account setup',
    setupSignIn: 'Already finished setup? Sign in',
    verificationDescription: 'Enter the code from your email at {email}, then set and confirm your Spatius account password to finish setting up your partner-managed account.',
    verificationReady: 'Account setup is ready to continue. Use the code from your email, or request a new code.',
    registrationPassword: 'Set your Spatius account password',
    verificationRetry: 'Request a new code and enter your password again to retry account setup.',
    missing: 'Open the original invitation link to continue.',
    unavailable: 'This invitation is unavailable. Ask your partner for a new link.',
    used: 'This invitation has already been used. Sign in to your Studio account.',
    managedBy: 'Managed by {vendor}',
  },
  common: {
    welcome: 'Welcome',
    login: 'Login',
    register: 'Register',
    logout: 'Logout',
    email: 'Email',
    password: 'Password',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    confirmPassword: 'Confirm Password',
    username: 'Username',
    nickname: 'Nickname',
    rememberMe: 'Remember me',
    forgotPassword: 'Forgot password?',
    submit: 'Submit',
    cancel: 'Cancel',
    loading: 'Loading...',
    success: 'Success',
    error: 'Error',
    required: 'Required',
    optional: 'Optional',
    back: 'Back',
    next: 'Next',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    create: 'Create',
    search: 'Search',
    filter: 'Filter',
    reset: 'Reset',
    actions: 'Actions',
    copyright: '© 2026 SPATIALWALK PTE. LTD., operating Spatius. All rights reserved.',
    studio: 'Studio',
    welcomeToStudio: 'Welcome to Spatius Studio',
    redirecting: 'Redirecting...',
    console: 'Console',
    getStarted: 'Get Started',
    learnMore: 'Learn More',
    confirm: 'Confirm',
    thisAvatar: 'this avatar',
    retry: 'Retry',
  },
  home: {
    hero: {
      title: 'Spatius Studio',
      subtitle: 'Powerful AI Digital Avatar Services for Developers',
      description: 'Client, Web, and Server SDKs to easily integrate AI digital avatar capabilities and start your digital avatar journey',
      login: 'Login',
      console: 'Go to Console',
    },
    features: {
      title: 'Studio Features',
      subtitle: 'Core capabilities we provide',
      sdk: {
        title: 'Cross-Platform SDKs',
        description: 'Client, Web, and Server SDKs for rapid integration',
        client: {
          title: 'Client SDK',
          description: 'iOS and Android support for easy mobile app integration',
        },
        web: {
          title: 'Web SDK',
          description: 'Browser integration with smooth Web experience',
        },
        server: {
          title: 'Server SDK',
          description: 'Multi-language server SDKs for backend integration',
        },
      },
      ai: {
        title: 'AI Digital Avatar Service',
        description: 'Powerful AI digital avatar capabilities with real-time interaction and personalization',
        realtime: {
          title: 'Real-time Interaction',
          description: 'Millisecond-level response with smooth natural conversation',
        },
        avatar: {
          title: 'Digital Avatar',
          description: 'Photorealistic 3D digital avatars with diverse customization',
        },
        voice: {
          title: 'Voice Sync',
          description: 'Precise phoneme synchronization with perfect lip and voice matching',
        },
      },
    },
    sdk: {
      title: 'Cross-Platform SDK Support',
      subtitle: 'Choose your platform and get started quickly',
      quickStart: 'Quick Start',
      docs: 'View Docs',
    },
    cta: {
      title: 'Start Your Digital Avatar Journey',
      subtitle: 'Register now and start using Spatius Studio',
      register: 'Register Now',
      login: 'Already have an account? Login now',
    },
  },
  auth: {
    title: {
      login: 'Welcome back',
      register: 'Create your account',
    },
    subtitle: {
      login: 'Enter your password to continue.',
      register: 'Looks like you\'re new. Pick a password and we\'ll get you set up.',
    },
    welcome: {
      title: 'Welcome to Spatius',
      subtitle: 'We\'ll sign you in or create an account if you don\'t have one yet.',
      emailPlaceholder: 'Write your e-mail',
      continue: 'Continue',
      or: 'Or',
      terms: 'By continuing with one of the options, you agree to the {terms} and confirm you have read {privacy}',
      termsText: 'Terms of Use',
      privacyText: 'Privacy policy',
    },
    email: {
      label: 'Email Address',
      placeholder: 'Write your e-mail',
      useAnother: 'Use a different email',
    },
    password: {
      label: 'Password',
      placeholder: 'Your password',
      newPlaceholder: 'New Password',
      confirmPlaceholder: 'Confirm New Password',
      minLength: 'At least 8 characters',
      confirm: 'Confirm Password',
      strength: 'Strength',
      weak: 'Weak',
      fair: 'Fair',
      good: 'Good',
      strong: 'Strong',
    },
    username: {
      label: 'Username (Optional)',
      placeholder: 'username',
    },
    nickname: {
      label: 'Nickname (Optional)',
      placeholder: 'nickname',
    },
    rememberMe: 'Remember me',
    forgotPassword: 'Forgot password?',
    loginButton: 'Sign In',
    registerButton: 'Create Account',
    loggingIn: 'Signing in...',
    registering: 'Creating account...',
    loginSuccess: 'Login successful',
    registerSuccess: 'Account created successfully',
    loginError: 'Login failed',
    registerError: 'Registration failed',
    noAccount: 'Don\'t have an account?',
    haveAccount: 'Already have an account?',
    signUp: 'Sign Up',
    signIn: 'Sign In',
    signin: {
      errorInvalid: 'Incorrect email or password.',
      signupPrompt: 'Don\'t have a Spatius account?',
      signupAction: 'Sign up now →',
    },
    signup: {
      title: 'Sign up',
      allFieldsRequired: '/All fields required',
      namePlaceholder: 'Your name',
      passwordStrength: 'Password strength',
      agreeToTerms: 'I have read, and agree to the {privacy}, {terms}, and Data Processing Agreement.',
      termsText: 'Terms of Service',
      privacyText: 'Privacy Policy',
      failed: 'Registration failed. Please try again.',
      signInToContinue: 'Try signing in to continue, or use a different email.',
      continueSignIn: 'Continue sign-in',
    },
    verify: {
      title: 'Create your account',
      titleReset: 'Reset your password',
      subtitle: 'We\'ve sent you a passcode. Please check your inbox at {email}.',
      resendCode: 'Resend code',
      restartSocial: 'Restart {provider} sign-in',
      resendCodeCooldown: 'Resend ({seconds}s)',
      verify: 'Verify',
      invalidCode: 'Invalid verification code',
      error: 'Verification failed',
      success: 'Verification successful',
      codeSent: 'Verification code sent',
      needVerification: 'Your email is not verified. Please verify your email before logging in',
    },
    reset: {
      title: 'Reset password.',
      subtitle: 'Enter your email to reset password.',
      button: 'Reset password',
      sent: 'Password reset email sent successfully',
      error: 'Failed to send password reset email',
    },
    resetPassword: {
      title: 'Reset Your Password',
      subtitle: 'Please enter your new password',
      button: 'Reset Password',
      success: 'Password reset successfully. Redirecting to login...',
      invalidToken: 'Invalid or expired reset token',
      backToLogin: 'Back to Login',
    },
    oauth: {
      github: 'Continue with GitHub',
      google: 'Continue with Google',
      connecting: 'Connecting...',
      error: 'OAuth login failed: {error}',
      divider: 'Or sign in with email',
      registerDivider: 'Or sign up with email',
      quickLogin: 'Quick login with third-party account',
      loginWith: 'Login with {provider}',
      securityNote: 'We use OAuth 2.0 security protocol and will not access your password information',
      clientIdNotConfigured: 'Client ID not configured',
      routeNotImplemented: 'OAuth login endpoint not implemented, please contact administrator',
    },
    validation: {
      turnstileRequired: 'Please complete the verification',
      turnstileError: 'Verification failed. Please try again.',
      emailRequired: 'Please enter your email address',
      passwordRequired: 'Please enter your password',
      passwordMinLength: 'Password must be at least 8 characters',
      passwordMismatch: 'Passwords do not match',
    },
  },
  cliAuth: {
    heroTitle: 'Your next step starts here.',
    heroSubtitle: 'Connect your terminal to Spatius and bring your next idea to life.',
    approvalHint: 'Only approve if you started this request. You’ll return to your local CLI session to finish signing in.',

    eyebrow: 'CLI Authorization',
    title: 'Authorize {clientName}',
    subtitle: 'Review this pending request, then approve it to sign the {clientName} CLI in to your Spatius account.',
    loading: 'Loading authorization request...',
    redirecting: 'Redirecting to login...',
    missingRequest: 'Authorization request not found.',
    unknownClient: 'CLI application',
    statusReady: 'Ready to approve',
    statusApproved: 'Approved',
    statusExpired: 'Expired',
    statusLabel: 'Status',
    clientLabel: 'Client',
    requestIdLabel: 'Request ID',
    expiresLabel: 'Expires',
    accountLabel: 'Account',
    expiresAt: 'Expires at {time}',
    accountMissing: 'Sign in to continue',
    signedInAs: 'You are signed in as {email}. Approving this request will redirect the browser back to your local CLI session and store a CLI session there.',
    signInHint: 'You need to sign in before approving this CLI authorization request.',
    approve: 'Approve and Continue',
    approving: 'Approving...',
    backToLogin: 'Sign In',
    retry: 'Retry',
    refresh: 'Refresh request',
    approvedMessage: 'This request has already been approved. Return to {clientName} if the browser does not redirect automatically.',
    expiredMessage: 'This authorization request has expired. Start the login flow again from {clientName}.',
    successRedirect: 'Approval succeeded. Redirecting back to {clientName}...',
    loadError: 'Failed to load authorization request',
    approveError: 'Failed to approve authorization request',
    missingRedirect: 'CLI authorization redirect is missing.',
    invalidRedirect: 'CLI authorization redirect must point to a local callback server.',
  },
  error: {
    network: 'Network error',
    unauthorized: 'Unauthorized, please login again',
    forbidden: 'Insufficient permissions',
    notFound: 'Resource not found',
    serverError: 'Server error',
    unknown: 'Unknown error',
  },
  navigation: {
    home: 'Home',
    avatars: 'Avatars',
    avatarLibrary: 'Public Avatars',
    customAvatar: 'Custom Avatars',
    createAvatar: 'Create Avatar',
    avatarUnits: 'Avatar Creations',
    account: 'Account',
    developer: 'Developer',
    integrate: 'Integrate',
    apiKeys: 'API Key',
    stats: 'Usage',
    connectionLogs: 'Logs',
    settings: 'Settings',
    documentation: 'Docs',
    community: 'Community',
    website: 'spatius.ai',
    pricing: 'Pricing',
    plan: 'Plan & Billing',
    billing: 'Billing',
    feedback: 'Feedback',
  },
  billing: {
    upgradePlan: 'Upgrade Plan',
    credits: 'Credits',
    currentPlan: 'Current Plan',
    renewsOn: 'Renews on {date}',
    refreshesOn: 'Refreshes on {date}',
    remainingCredit: 'Remaining Credit',
    estimatedRemaining: 'Estimated Remaining Minutes',
    concurrentSession: 'Max Concurrent Sessions',
    subscriptionManagement: 'Subscription Management',
    creditsRemaining: 'credits remaining',
    left: 'left',
    remaining: 'remaining',
    autoReload: {
      title: 'Auto-Reload Credits',
      description: 'Automatically top up credits when your balance is low.',
      reloadAmount: 'Reload Amount',
      thresholdLabel: 'When balance drops below',
      summary: 'Auto-reload will charge {amount} when balance drops below {threshold}, using your subscription payment method.',
      save: 'Save',
      saving: 'Saving...',
      saved: 'Auto-reload settings saved',
    },
  },
  language: {
    zhCN: '中文',
    enUS: 'English',
    switch: 'Switch Language',
  },
  apiKeys: {
    title: 'API keys',
    subtitle: 'Create and manage your API keys',
    securityInfo: 'The list contains all of your API keys. Please keep them safe and do not share them with others or expose them in browser or other client code. To protect your account security, we may automatically disable API keys that are found to be publicly leaked.',
    securityInfoShort: 'Keep API keys safe and do not expose them.',
    loading: 'Loading...',
    empty: 'No API key available, you can',
    createLink: 'create an API key',
    createButton: 'Create new API key',
    createTitle: 'Create API key',
    selectApp: 'Select Application',
    selectAppPlaceholder: 'Please select an application',
    selectAppRequired: 'Please select an application',
    name: 'Name',
    namePlaceholder: 'Your App Name',
    copyWarning: 'Please copy and save your app secret immediately.',
    copy: 'Copy',
    reveal: 'Reveal',
    hide: 'Hide',
    copySuccess: 'API key copied to clipboard',
    copyFailed: 'Failed to copy API key',
    createSuccess: 'API key created successfully',
    createFailed: 'Failed to create API key, please try again',
    keyNotAvailable: 'API key not available',
    delete: 'Delete',
    deleteTitle: 'Delete API key',
    deleteWarning: 'The API key will be disabled immediately. API requests made using this key will be rejected, which may cause any systems still relying on it to crash. Once deleted, you will no longer be able to view or modify this API key',
    deleteNamePrompt: 'Please enter the name of the key to delete :',
    deleteNameHint: 'Enter "{name}" to confirm deletion',
    deleteNameMismatch: 'The name does not match',
    deleteButton: 'Delete this API key',
    deleteSuccess: 'API key deleted successfully',
    table: {
      name: 'Name',
      appSecret: 'Api Key',
      creationDate: 'Creation date',
      latestUseDate: 'Latest use date',
      manage: 'Manage',
    },
  },
  apiKeyManagement: {
    title: 'Management',
    subtitle: 'Manage applications and API keys',
    create: 'Create',
    createTitle: 'Create New Application',
    createDesc: 'Create a new application to manage API keys',
    createApiKeyCta: 'Create api key',
    createResult: {
      title: 'API Credentials',
      description: 'Please copy and save these credentials.',
      environmentVariable: 'Environment variables',
      copyField: 'Copy {field}',
      copiedField: '{field} copied',
    },
    name: 'Application Name',
    namePlaceholder: 'Enter application name',
    nameRequired: 'Please enter application name',
    loading: 'Loading...',
    noData: 'No data',
    apiKeyCount: 'API Key Count',
    detailButton: 'Details',
    confirmDeleteTitle: 'Confirm Delete Application',
    confirmDeleteDesc: 'This action cannot be undone. All API keys under this application will also be deleted.',
    confirmDeleteMessage: 'Are you sure you want to delete application "{name}"? This action cannot be undone. All API keys under this application will also be deleted.',
    list: {
      empty: 'No applications',
      emptyDesc: 'Create your first application to get started',
      appId: 'App ID',
      name: 'Name',
      apiKeys: 'API Keys',
      createdAt: 'Created At',
      actions: 'Actions',
      view: 'Manage',
      manageApiKeys: 'Manage API Keys',
      delete: 'Delete',
      confirmDelete: 'Are you sure you want to delete this application?',
      deleteSuccess: 'Application deleted successfully',
      createSuccess: 'Application created successfully',
    },
    detail: {
      title: 'Application Details',
      subtitle: 'Manage application information and API Keys',
      back: 'Back to List',
      backToList: 'Back to Applications',
      loading: 'Loading...',
      notFound: 'Application not found or has been deleted',
      info: 'Basic Information',
      infoTitle: 'Application Information',
      appId: 'App ID',
      name: 'Application Name',
      apiKeyCount: 'API Key Count',
      createdAt: 'Created At',
      createdAtLabel: 'Created At:',
      apiKeys: 'API Key List',
      apiKeysDesc: 'Manage application API Keys',
      createKey: 'Add API Key',
      createKeyTitle: 'Add API Key',
      createKeyDesc: 'Create a new API Key for application "{name}"',
      createKeyMessage: 'Click confirm to generate a new API Key for this application.',
      creating: 'Creating...',
      confirmCreate: 'Confirm Create',
      copySuccess: 'Copied to clipboard',
      copyFailed: 'Copy failed',
      copy: 'Copy',
      keyList: {
        loading: 'Loading...',
        empty: 'No API Keys, click the button above to add',
        emptyDesc: 'Create an API key to get started',
        key: 'API Key',
        createdAt: 'Created At',
        createdBy: 'Created By',
        actions: 'Actions',
        delete: 'Delete',
        deleteTitle: 'Delete API Key',
        confirmDelete: 'Are you sure you want to delete API Key "{key}"? This action cannot be undone. Applications using this API Key will stop working.',
        confirmDeleteDefault: 'Are you sure you want to delete this API Key? This action cannot be undone. Applications using this API Key will stop working.',
        deleteSuccess: 'API Key deleted successfully',
        deleting: 'Deleting',
        deleteFailed: 'Failed to fetch application information',
        loadFailed: 'Failed to load API key list',
        maskedKey: '••••••••••••••••',
      },
      sessionToken: {
        title: 'Temporary Token',
        warning: 'For development and debugging only. In production environment, please generate through the server-side.',
        warningDesc: 'The Session Token generated by this tool is used to run through the Client SDK process without a developed backend. For security reasons, the Token\'s validity period is forcibly set to 24 hours, and there is a generation frequency limit per user.',
        generateButton: 'Generate Temporary Token',
        generateHint: '*Please copy immediately after generation; it will be lost after refreshing the page',
        sessionToken: 'SESSION TOKEN',
        validUntil: 'Valid until',
        validDuration: '24 hours',
        copy: 'Copy',
        regenerate: 'Regenerate',
        generating: 'Generating...',
        generateFailed: 'Generation failed, please try again later',
        noApiKey: 'Please create an API Key first before generating a Token',
        invalidApiKey: 'Invalid API Key',
        copied: 'Copied',
      },
    },
  },
  settings: {
    title: 'Settings',
    description: 'Manage your account settings and preferences',
    accountInfo: {
      title: 'Account Information',
      description: 'View and manage your account information',
      notLoggedIn: 'Not logged in',
      username: 'Username',
      loginMethod: 'Login Method',
      oauthLogin: 'Third-party login',
    },
    password: {
      title: 'Change Password',
      description: 'Regularly update your password to keep your account secure',
      security: 'Password Security',
      updatePassword: 'Update your account password',
      changeButton: 'Change Password',
      dialogTitle: 'Change Password',
      dialogDescription: 'Please enter your current password and new password',
      oldPassword: 'Current Password',
      oldPasswordPlaceholder: 'Enter your current password',
      newPassword: 'New Password',
      newPasswordPlaceholder: 'Enter new password (at least 8 characters)',
      confirmPassword: 'Confirm New Password',
      confirmPasswordPlaceholder: 'Enter new password again',
      allFieldsRequired: 'Please fill in all fields',
      minLength: 'New password must be at least 8 characters',
      mismatch: 'New passwords do not match',
      changeSuccess: 'Password changed successfully',
      changeFailed: 'Failed to change password',
      cancel: 'Cancel',
      confirm: 'Confirm Change',
    },
    language: {
      title: 'Language Settings',
      description: 'Select interface display language',
      currentLanguage: 'Current Language',
    },
    accountActions: {
      title: 'Account Actions',
      description: 'Manage your account actions',
      logout: 'Logout',
      logoutDescription: 'Safely log out of your account',
      logoutButton: 'Logout',
    },
    logout: {
      success: 'Logged out successfully',
    },
    comingSoon: {
      title: 'More Features',
      description: 'More settings options coming soon',
      message: 'More settings features are under development, stay tuned',
    },
  },
  cookie: {
    title: 'Cookie Consent',
    description: 'We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. By continuing to use this site, you consent to our use of cookies.',
    accept: 'Accept',
    reject: 'Reject',
    accepted: 'Cookie consent accepted',
    rejected: 'Cookie consent rejected. We will only use essential functional cookies and will not collect analytics data.',
    info: 'Even if you reject cookies, we will still use essential functional cookies to ensure the website works properly (such as login status).',
  },
  avatars: {
    desktopOnly: {
      title: 'Continue on desktop',
      description: 'Creating and previewing avatars is not supported on mobile. Please open Studio on your computer to continue.',
      confirm: 'Got it',
    },
    list: {
      title: 'Custom Avatars',
      subtitle: 'Manage your custom avatar assets',
      createButton: 'Create Avatar',
      createFromPhoto: 'Start from a photo',
      all: 'All',
      completed: 'Completed',
      generating: 'Generating',
      failed: 'Failed',
      public: 'Public',
      showPublic: 'Show Public Avatars',
      loading: 'Loading assets...',
      loadMore: 'Load more',
      loadingMore: 'Loading more...',
      noAssets: 'No assets yet.',
      noAssetsFiltered: 'No {filter} assets found.',
      generatingAvatar: 'Generating Avatar',
      creating: 'Creating',
      creatingStageSubmitted: 'Submitted',
      creatingStagePreparing: 'Preparing your avatar',
      creatingStageBuilding: 'Building your avatar',
      creatingStageFinalizing: 'Finalizing your avatar',
      creatingStageReady: 'Ready to view',
      creatingStageFailed: 'Creation unsuccessful',
      creatingSummaryTitleOne: '1 avatar is being created',
      creatingSummaryTitleMany: '{count} avatars are being created',
      creatingOverallHint: 'Most avatars are ready in 3–4 hours. You can leave this page and come back later.',
      creationInProgress: 'Creation in progress',
      startedJustNow: 'Started just now',
      startedMinutesAgo: 'Started {count}m ago',
      startedHoursAgo: 'Started {count}h ago',
      edit: 'Edit',
      viewEdit: 'View / Edit',
      previewEdit: 'Preview / Edit',
      idCopied: 'Copied',
      actionsFor: 'Avatar actions for {name}',
      view: 'View',
      previewTalk: 'Preview / Talk',
      tuneAvatar: 'Tune avatar',
      tryChat: 'Try live chat',
      countLabel: '{count} avatars',
      copy: 'Copy',
      duplicate: 'Duplicate',
      delete: 'Delete',
      id: 'ID',
      copyId: 'Copy ID: {id}',
      copyIdLabel: 'Copy ID',
      copyIdSuccess: 'Avatar ID copied to clipboard',
      copyIdFailed: 'Failed to copy Avatar ID',
      copySuccess: 'Avatar copied successfully',
      copyFailed: 'Failed to copy avatar',
      duplicateSuccess: 'Avatar duplicated successfully',
      duplicateFailed: 'Failed to duplicate avatar',
      duplicateTitle: 'Duplicate Avatar',
      duplicateDescription: 'Are you sure you want to duplicate "{name}"? A new avatar will be created with the same settings.',
      deleteSuccess: 'Avatar deleted successfully',
      deleteFailed: 'Failed to delete avatar',
      deleteTitle: 'Delete Avatar',
      deleteDescription: 'Are you sure you want to delete "{name}"? This action cannot be undone.',
      deleteNoRefundTitle: 'Delete "{name}"',
      deleteGeneratingTitle: 'Delete "{name}" while it is being created',
      deleteFailedRecordTitle: 'Remove the failed record for "{name}"',
      deleteNoRefundHeadline: 'Deleting does not return the Avatar Creation',
      deleteNoRefundBody: 'The Avatar Creation used to create this avatar is spent and will not come back. Avatar Creations are only returned when a creation fails.',
      deleteGeneratingBody: 'This creation is still running. The Avatar Creation it used will not come back, and you will not get this avatar.',
      deleteBalanceUnchanged: 'Your balance stays at {count} after deleting.',
      deleteIrreversible: 'This cannot be undone.',
      deleteAcknowledge: 'I understand deleting does not return the Avatar Creation',
      deleteFailedRecordBody: 'This only takes the record off your list. The Avatar Creation this failed run used was already returned automatically — your balance is not affected.',
      deleteConfirm: 'Delete avatar',
      removeRecord: 'Remove record',
      download: 'Download',
      downloadSuccess: 'Asset package downloaded successfully',
      downloadFailed: 'Failed to download asset package',
      fetchFailed: 'Failed to fetch assets',
      fetchGeneratingFailed: 'Failed to fetch generating avatars',
      fetchAvatarsFailed: 'Failed to fetch avatars',
    },
    publicConversation: {
      dockTitle: 'Talk to this avatar',
      dockSubtitle: 'Official avatar · {voice}',
      publicAvatar: 'Public avatar',
      stageKicker: '01 · EXPERIENCE',
      stageTitle: 'Try this avatar',
      integrationKicker: '02 · INTEGRATION',
      integrationTitle: 'Use it in your app',
      avatarIdTitle: 'Avatar ID',
      backgroundTitle: 'Background',
      integrationNote: 'Public avatars are ready to use — drive this one from your own app with AvatarKit and the ID above.',
      fallbackName: 'Public avatar',
      loadingDetails: 'Loading avatar details...',
      initializing: 'Initializing AvatarKit...',
      downloading: 'Downloading avatar resources...',
      loadingAvatar: 'Loading avatar...',
      creatingView: 'Creating avatar view...',
      preparing: 'Preparing avatar...',
      notFound: 'This public avatar is unavailable.',
    },
    download: {
      title: 'Select Quality',
      description: 'Please select at least one quality level (Standard, High, Ultra)',
      download: 'Download',
      quality: {
        sd: 'Standard',
        hd: 'High',
        uhd: 'Ultra',
      },
    },
    edit: {
      title: 'Edit Avatar',
      cancel: 'Cancel',
      saving: 'Saving...',
      save: 'Save',
      avatarPreview: 'Avatar Preview',
      minSdkVersion: 'Min. SDK Version',
      config: 'Config',
      postprocess: 'Post-processing',
      reset: 'Reset',
      avatarId: 'Asset 1: Avatar ID',
      avatarName: 'Avatar name',
      namePlaceholder: 'Enter character name',
      dptVersion: 'DPT Version',
      loading: 'Loading...',
      selectDptVersion: 'Select DPT version',
      camera: 'Camera',
      fovY: 'fovY (radians)',
      translationX: 'translationX (m)',
      translationY: 'translationY (m)',
      translationZ: 'translationZ (m)',
      modelParams: 'Model Parameters (applied to current frame)',
      eyeBlink: 'eyeBlink',
      expressionWeight: 'expressionWeight',
      rotation: 'rotation (radian offset)',
      neckPose: 'neckPose (radian offset)',
      jawPose: 'jawPose (radian offset)',
      weight: 'weight',
      invalidAvatarId: 'Invalid avatar ID',
      avatarNotFound: 'Avatar not found',
      unknownAvatar: 'Unknown Avatar',
      characterDataAbnormal: 'This character\'s data is abnormal. Please submit a support ticket.',
      pleaseEnterName: 'Please enter a name',
      savedSuccessfully: 'Saved successfully',
      updateFailed: 'Failed to update avatar',
      download: 'Download',
      downloadSuccess: 'Asset package downloaded successfully',
      downloadFailed: 'Failed to download asset package',
      loadFailed: 'Failed to load avatar',
      backgroundAssetTitle: 'Background',
      backgroundAssetDesc: 'Generated background image.',
      backgroundImageAlt: 'Generated 16:9 avatar background preview',
      showBackground: 'Show Background',
      backgroundGuide: 'Guide',
      downloadBackground: 'Download',
      backgroundOpenedInNewTab: 'Background image opened in a new tab',
      postprocessRangeExceeded: 'Value out of range',
      postprocessRangeExceededDescription: 'Allowed range is {min} to {max}. Adjusted to {adjusted}.',
      stage: {
        kicker: '01 · EXPERIENCE',
        title: 'Meet your avatar',
      },
      delivery: {
        kicker: '02 · DELIVERY',
        title: 'Your delivery package',
        forAvatarKit: 'For AvatarKit',
        copy: 'Copy',
        copied: 'Copied',
        copyFailed: 'Failed to copy',
        backgroundTitle: 'Asset 2: Background',
        backgroundMaster: '16:9 master',
        integrationGuide: 'Integration guide',
        sdkNote: 'AvatarKit does not load the background automatically.',
        zoomTitle: '16:9 master image',
      },
      dock: {
        connectTimeout: 'Connection timed out. Please start the conversation again.',
        disconnected: 'The conversation was disconnected.',
        idleTitle: 'Talk to this avatar',
        greetingTitle: 'Greeting…',
        greetingMutedTitle: 'Greeting (muted)',
        greetingMutedHint: 'Autoplay was blocked — tap the sound badge to unmute',
        startConversation: 'Start',
        endConversation: 'End conversation',
        connecting: 'Connecting…',
        listening: 'Listening — say something',
        speaking: 'Responding…',
        demoLabel: 'Studio demo · {voice}',
        changeVoice: 'Change voice',
        muted: 'Muted',
        mute: 'Mute',
        unmute: 'Unmute',
        remainingTitle: 'Time left in this conversation',
        remainingAriaLabel: 'Time left in this conversation: {time}',
        endingSoon: 'Less than one minute remaining',
        timeLimitTitle: 'Conversation time is up',
        timeLimitDescription: 'The available time for this conversation has ended, so the connection was closed. You can start a new conversation at any time.',
        timeLimitDetailsLabel: 'Why did this conversation end?',
        startAgain: 'Restart',
        inputPlaceholder: 'Prefer typing? Send a message',
        send: 'Send',
      },
      demoSetup: {
        kicker: 'DEMO SETUP',
        title: 'Pick a voice',
        disclaimer: 'These settings only apply to demos inside Studio. Spatius does not provide LLM or TTS services for production integration.',
        voice: {
          male: 'Masculine',
          female: 'Feminine',
        },
      },
      pp: {
        kicker: '03 · OPTIMIZE',
        entryTitle: 'Post-processing',
        collapse: 'Close',
        settingsCount: '3 settings',
        notice: 'Adjust while chatting to see the effect.',
        expressiveness: {
          title: 'Expressiveness',
          desc: 'Adjusts expression, head and mouth movement together.',
          less: 'Subtle',
          default: 'Default',
          more: 'Vivid',
        },
        head: {
          title: 'Head Adjustment',
          desc: 'Fine-tune the head\'s baseline pose in three directions.',
          tilt: 'Up / down',
          tiltMin: 'Head down',
          tiltMax: 'Head up',
          turn: 'Turn',
          turnMin: 'Look left',
          turnMax: 'Look right',
          roll: 'Side tilt',
          rollMin: 'Tilt left',
          rollMax: 'Tilt right',
        },
        jaw: {
          title: 'Jaw Height',
          desc: 'Corrects the mouth\'s baseline position.',
          min: 'Lower',
          default: 'Default',
          max: 'Higher',
        },
        placement: {
          title: 'Avatar Placement',
          desc: 'Updates instantly and applies to all aspect ratios.',
          scale: 'Size',
          scaleMin: 'Smaller',
          scaleMax: 'Larger',
          horizontal: 'Horizontal',
          horizontalMin: 'Left',
          horizontalMax: 'Right',
          vertical: 'Vertical',
          verticalMin: 'Down',
          verticalMax: 'Up',
          instant: 'Applies instantly',
        },
      },
      feedback: {
        question: 'How close is this avatar to being usable?',
        autoAttach: 'Automatically linked to this avatar',
        anchors: {
          0: 'Unusable',
          2: 'Serious issues',
          4: 'Needs clear fixes',
          6: 'Ready to use',
          8: 'Looks great',
          10: 'Stunning',
        },
        issueQuestion: 'What\'s wrong? Pick at least one',
        issues: {
          lipSync: 'Lip sync off',
          teeth: 'Teeth artifacts',
          face: 'Face artifacts',
          body: 'Body artifacts',
          background: 'Background issues',
          motion: 'Unnatural motion',
          other: 'Other',
        },
        noteLabel: 'Details',
        notePlaceholder: 'Briefly describe the issue',
        savedHint: 'Saved — thanks for the feedback',
        closeLabel: 'Close rating',
        submit: 'Submit',
      },
      publicApply: {
        inviteTitle: 'Looking this good — publish it to the avatar library?',
        inviteDesc: 'Pass review to earn Avatar Creations. A copy is published; your own avatar stays private.',
        inviteDismiss: 'Not now',
        inviteGo: 'Apply',
        dialogKicker: 'PUBLIC AVATAR',
        dialogTitle: 'Publish to the avatar library',
        explainCopy: 'After you submit, we will copy your avatar into the public library.',
        explainDrive: 'Once public, other developers can drive this copy.',
        explainPrivate: 'Your own avatar stays private and cannot be driven by others. The public copy is a snapshot at review time; later parameter changes will not sync to it.',
        explainReview: 'Applications are reviewed manually. Avatar Creations are granted after approval, and the result will be posted in Feedback.',
        consentStatement: 'I understand and agree: once public, other developers can drive this copy of my avatar.',
        fieldAvatar: 'Avatar',
        cancel: 'Cancel',
        close: 'Close',
        submit: 'Submit application',
        submitSuccess: 'Application submitted. Track progress in Feedback.',
      },
    },
    create: {
      emotion: {
        reviewTitle: 'Review and create',
        ownershipTitle: 'Asset ownership & privacy',
        title: 'Avatar emotion',
        neutral: 'Neutral',
        happy: 'Happy',
        sad: 'Sad',
        description: 'Emotion cannot be switched after creation.',
      },
      title: 'Create Avatar',
      subtitle: 'Create a new avatar',
      backToList: 'Back to List',
      comingSoon: 'Coming Soon',
      clickToUpload: 'Click to upload image',
      imageFormat: 'JPG or non-transparent PNG, up to 10MB',
      materialSuggestions: 'Material Suggestions',
      materialSuggestionsDesc: 'High-resolution, evenly lit front-facing photos. Avoid wearing glasses or bangs that obscure facial features.',
      viewFullGuide: 'View full guide',
      clickToEnlarge: 'Click to enlarge',
      examples: 'Examples',
      name: 'Name',
      namePlaceholder: 'e.g., Smart Assistant',
      systemPrompt: 'System Prompt',
      systemPromptPlaceholder: 'Enter system prompt for the character...',
      aiProcessing: 'AI Processing',
      aiProcessingDesc: 'Automatically adjusts body and face orientation to a standard front-facing pose for optimal avatar generation.',
      portraitRights: 'Portrait Rights Agreement',
      portraitRightsDesc: 'I confirm that I have the right to use this photo and consent to its use in creating an avatar.',
      createButton: 'Create Avatar',
      creating: 'Creating...',
      cropImage: 'Crop Image',
      cropImageDesc: 'Please adjust the image cropping area',
      failedToLoadImage: 'Failed to load image',
      failedToReadImage: 'Failed to read image file',
      pleaseSelectImage: 'Please select an image file',
      imageTooLowResolution: 'Image resolution is too low (minimum short side: 340px)',
      pleaseEnterName: 'Please enter avatar name',
      pleaseEnterPrompt: 'Please enter system prompt',
      pleaseUploadImage: 'Please upload an image',
      pleaseAgreeToTerms: 'Please confirm your right to use this photo to create an avatar',
      uploadingImage: 'Uploading image...',
      creatingAvatar: 'Creating avatar...',
      createdSuccessfully: 'Avatar creation submitted successfully.',
      createFailed: 'Failed to create avatar',
      noImageSelected: 'No image selected',
      noPermission: 'You do not have permission to create characters',
      limitedBeta: {
        title: 'Create Avatar is currently in limited beta',
        description: 'We are opening 100 complimentary Avatar Creations to selected users and teams during this beta period.',
        instructionsIntro: 'To request access, please email',
        instructionsSuffix: 'and tell us:',
        who: 'Who you are: an individual or a company',
        company: 'Your company name, if applicable',
        useCase: 'How you plan to use Create Avatar',
        response: 'Our team will review your request and get back to you as soon as possible.',
      },
      confirmTitle: 'Confirm creation',
      confirmMessage: 'This will use 1 Avatar Creation.',
      // Draft copy — final wording pending PM review (req 8).
      confirmTimeline: 'Creation takes 3–4 hours. You can close this page — check progress anytime in Custom Avatars.',
      confirmCancel: 'Cancel',
      confirmOk: 'Confirm & create',
      selectEngine: 'Select Generation Engine',
      baseTemplate: 'Base Template',
      baseTemplateDesc: 'Choose the template that best matches your uploaded character.',
      imageSizeLimit: 'Image size should be less than 10MB',
      coreDesc: 'Best for standard frontal photos — face and body directly facing the camera, hands not visible.',
      flexDesc: 'Best for photos with visible hands, tilted poses, or body not fully facing the camera.',
      presetEastAsianMale: 'East Asian Male',
      presetLatinFemale: 'Latin Female',
      presetMateo: 'Mateo',
      presetWhiteHairedFemale: 'White-Haired Female',
      audioPresetEnglishMale: 'Male voice 1',
      audioPresetEnglishFemale: 'Female voice 1',
      audioPresetChineseMale: 'Male voice 2',
      audioPresetChineseFemale: 'Female voice 2',
      preprocess: {
        module: {
          detectDone: 'Photo verified',
          processDone: 'Selected',
        },
        photoGuide: {
          outputs: {
            eyebrow: 'What you\'ll get',
            sourceLabel: 'Your photo',
            avatarTitle: '3D Avatar',
            backgroundTitle: '16:9 background',
            finalTitle: 'Final result',
            expectTitle: 'What to expect',
            expect: {
              drive: 'Any-language audio drive · synced lips',
              expression: 'Natural expressions · stable subject',
              fidelity: 'Keeps your photo\'s look and style',
              body: 'Body fixed · no limb motion',
            },
          },
          mobileSummary: {
            title: 'You\'ll get two companion assets',
            body: 'A 3D avatar plus a 16:9 background from your photo\'s scene — combined and driven by audio. Creation takes 3–4 hours.',
          },
          rules: {
            title: 'Before you upload',
            compactTitle: 'Photo tips',
            tabReal: 'Real person',
            tabStylized: 'Cartoon character',
            real: {
              framing: 'Headshot or upper body, centered with margin, full head in frame.',
              facing: 'Face the camera; body turned no more than 25°, head upright.',
              subject: 'One adult subject; face, neck and shoulders unobstructed.',
            },
            stylized: {
              scope: 'One cartoon character with human structure — animals, plush toys and other non-human subjects are not supported.',
              features: 'Face shape and features close to human proportions — no extreme round faces or oversized eyes.',
              framing: 'Headshot or upper-body framing, centered and front-facing, full head visible.',
            },
          },
          examples: {
            goodBadge: 'Good',
            avoidBadge: 'Avoid',
            short: {
              real: {
                good: 'Good framing',
                crop: 'Head cut off',
                side: 'Turned away',
                occlusion: 'Face covered',
              },
              stylized: {
                good: 'Good example',
                nonhumanAnimal: 'Animal subject',
                nonhumanRobot: 'Robot subject',
                face: 'Non-human features',
              },
            },
            real: {
              good: 'Recommended framing: centered, front-facing, full head and shoulders clear',
              crop: 'Head cut off by the frame — the system must invent the missing parts',
              side: 'Turned well away from the camera — rebuilding the hidden side can change identity',
              occlusion: 'Hand covering cheek and jaw — removing it means rebuilding facial detail',
            },
            stylized: {
              good: 'Human-structured cartoon character, centered and front-facing',
              nonhumanAnimal: 'Animal subjects are not supported',
              nonhumanRobot: 'Robots and mechs are not supported',
              face: 'Features far from human proportions — oversized eyes, minimal nose or mouth',
            },
          },
          details: {
            toggle: 'More tips',
            real: {
              lens: { title: 'Source & lens', body: 'Use a high-resolution original photo (short side 1080px+); avoid video frames, close-up selfies and wide-angle distortion.' },
              faceLight: { title: 'Face & lighting', body: 'Keep natural skin texture and catchlights; soft, even lighting without harsh shadows, overexposure or heavy retouching.' },
              clothing: { title: 'Clothing & accessories', body: 'Avoid ear/neck accessories, busy patterns and clothing with visible weave texture.' },
              handsBackground: { title: 'Hands & background', body: 'Keep hands out of frame; background perspective should be natural, with light direction and tone matching the subject.' },
            },
            stylized: {
              features: { title: 'Facial proportions', body: 'Eyes close to human size and shape, with a human-structured nose and lips that have volume.' },
              outline: { title: 'Outline & occlusion', body: 'Full head, face, neck and shoulders clearly visible — not covered by stray hair or objects.' },
              originality: { title: 'Originality & IP', body: 'Use original characters; avoid well-known anime, film or game IP.' },
              lightBackground: { title: 'Light & background', body: 'Keep character lighting even, with the background light source and tone consistent with the character.' },
            },
          },
        },
        upload: {
          headline: 'Upload a photo',
          subline: 'Click or drop here · JPG or non-transparent PNG · up to 10 MB · short side ≥ 340px, 1080px+ recommended',
          processNote: 'We check your photo first — problems are flagged before anything is created or spent.',
          notAnImage: 'Please choose an image file.',
          unsupportedFormat: 'Please upload a JPG or PNG image.',
          transparentPng: 'PNG images with transparent backgrounds are not supported. Please export with a solid background.',
          tooLarge: 'Image must be smaller than 10 MB.',
        },
        detect: {
          eyebrow: 'Photo check',
          headline: 'Checking your photo',
          previewAlt: 'Uploaded preview',
          waitingSubline: 'Waiting for the verification result...',
          finalCheck: 'Final check',
          waitingShort: 'Waiting for result',
          checking: 'Checking',
          rowChecked: 'Checked',
          row: {
            image_quality: 'Image quality',
            face_detection: 'Face detection',
            subject_verification: 'Subject verification',
            lighting_occlusion: 'Lighting & occlusion',
            composition: 'Composition',
          },
        },
        report: {
          eyebrow: 'Check result',
          headline: 'A few things need fixing',
          subline: 'Your photo isn\'t at the avatar standard yet — confirm and we\'ll handle it.',
          confirmButton: 'Start processing',
          reuploadButton: 'Upload another photo',
          action: {
            'crop': 'Crop to standard framing',
            'cleanup': 'Clean up background & lighting',
            'pose-fix': 'Adjust pose',
            'reframe': 'Reframe the shot',
            'occlusion-fix': 'Remove obstructions',
          },
        },
        process: {
          eyebrow: 'Asset preparation',
          eyebrowChoice: 'Select image',
          eyebrowFinalizing: 'Background preparation',
          headlineDual: 'Preparing two versions',
          headlineSingle: 'Refining your photo',
          headlineGenerateCompare: 'Generating the AI version',
          sublineDual: 'Both versions are revealed together once they\'re ready.',
          sublineGenerateCompare: 'Your original passes as-is — compare and pick once the AI version is ready.',
          refinedFallbackRow: 'Standardizing the image',
          rowNone: 'Nothing to fix',
          generationWorking: 'Generating…',
          generationWorkingSub: 'To the gold standard',
          refinedWorking: 'Refining…',
          refinedWorkingSub: 'Fixing your original',
          generationStep: {
            analyze: 'Analyzing composition & quality',
            redraw: 'Redrawing the subject',
            verify: 'Quality check',
          },
          track: {
            ready: 'Ready',
            originalReady: 'All clear',
            stillWorking: 'Still processing',
            failedShort: 'Not available this time',
            failedSub: 'Details with the results',
          },
          row: {
            'crop': 'Cropping to standard framing',
            'cleanup': 'Cleaning background & light',
            'pose-fix': 'Adjusting pose',
            'reframe': 'Reframing the shot',
            'occlusion-fix': 'Removing obstructions',
          },
          variant: {
            refined: {
              title: 'Refined',
              desc: 'Small corrections on your original photo — stays closest to it.',
            },
            generated: {
              title: 'AI generated',
              desc: 'Rebuilt to our gold standard — the most reliable for reconstruction.',
            },
            original: {
              title: 'Original',
              desc: 'Passed every check — exactly as you uploaded it.',
            },
          },
          choice: {
            headline: 'Pick the version to use',
            subline: 'Each has its strengths; the one you pick continues into your avatar.',
            headlineSingle: 'Confirm the result',
            sublineSingle: 'Confirming continues avatar creation with this photo.',
            headlineOneMade: 'One version made it',
            sublineOneMade: 'The other version didn\'t finish — reason on its card. Continue with the finished one as usual.',
            headlineBothFailed: 'Neither version made it',
            sublineBothFailed: 'This run didn\'t produce a usable result. Retrying won\'t consume Avatar Creations.',
            retryButton: 'Retry processing',
            slotFailed: 'Didn\'t finish',
            confirmButton: 'Use this one',
            confirmButtonSingle: 'Looks good, continue',
            zoom: 'Zoom in',
            zoomClose: 'Close preview',
          },
          trackFail: {
            processing_error: 'Processing hit an error and couldn\'t produce this version',
            quality_gate: 'The result didn\'t pass the quality check and was discarded',
            content_policy_sexual: 'This image may contain nudity or adult content. Please use another image.',
            content_policy_violence_dangerous: 'This image may contain violent, dangerous, or other restricted content. Please use another image.',
            content_policy_hate_harassment: 'This image may trigger hate or harassment safety rules. Please use another image.',
            content_policy_child_safety: 'This image may trigger child-safety rules. Please use another image.',
            content_policy_privacy: 'This image may contain sensitive personal information. Please use another image.',
            content_policy_ip: 'This image may trigger copyright or protected-content rules. Please use another image.',
            content_policy_other: 'This image may trigger content-safety rules. Please use another image.',
            input_invalid: 'The image cannot be read or does not meet the format or size requirements. Please upload it again.',
            input_unavailable: 'We cannot read this image right now. Please upload it again or try later.',
            model_no_output: 'No usable result was generated. Please use another image with a clearer person.',
            provider_busy: 'The processing service is temporarily busy. Please retry later; you do not need to replace the image.',
            provider_configuration: 'The processing service is temporarily unavailable. We are working on it; please try later.',
            output_invalid: 'The generated result did not pass format validation. Please retry later.',
          },
          finalizing: {
            title: 'Extracting background…',
            tileSub: 'Matching avatar presets',
            subline: 'Usually takes about a minute — then you\'ll confirm and name your avatar.',
          },
        },
        restore: {
          title: 'Picking up where you left off',
          subline: 'We found your previous photo processing task and are checking its progress. This won\'t consume anything.',
          goneToast: 'Your previous processing task is no longer available. Please upload a photo to start again.',
          unavailableToast: 'We couldn\'t check your previous task right now. It will be picked up next time you open this page.',
        },
        systemError: {
          title: 'Something went wrong — please retry',
          subline: 'The processing service hit an error. Please retry with the same photo, or upload another one.',
          notYourFault: 'This is on our side. Retrying won\'t consume Avatar Creations.',
          createUnknownTitle: 'We couldn\'t confirm whether processing started',
          createUnknownSubline: 'The request timed out after it was sent, so retrying it automatically could create duplicate work.',
          createUnknownNote: 'Upload the photo again only if you want to start a new processing task.',
          taskUnavailableTitle: 'This processing task is no longer available',
          taskUnavailableSubline: 'The service can no longer read this task. Upload the photo again to start a new one.',
          taskUnavailableNote: 'No Avatar Creation was consumed.',
          retryButton: 'Retry',
          retryingButton: 'Reconnecting…',
        },
        completion: {
          eyebrow: 'Background preparation',
          headlineDefault: 'Looks great!',
          headlineCropped: 'Framing adjusted automatically',
          headlineAdjusted: 'Looks great!',
          sublineDefault: 'Let\'s name your avatar.',
          sublineCropped: 'We tidied up the framing. Let\'s name your avatar.',
          sublineWithBackground: 'Your avatar photo and background are ready — review them, then name your avatar.',
          finalAlt: 'Processed photo',
          characterLabel: 'Avatar Photo',
          backgroundLabel: 'Avatar Background',
          backgroundAlt: 'Avatar background',
          backgroundFailed: 'Background not ready yet',
          backgroundFailedHint: 'You can still create the avatar — the background keeps being extracted after creation.',
          backgroundPending: 'Loading background',
          ownershipNote: 'This avatar is your own asset — visible and usable only under your account; Spatius won\'t publish or reuse it.',
          readyBadge: 'Ready',
        },
        discard: {
          action: 'Upload another photo',
          title: 'Discard this processing?',
          description: 'Progress, results and anything you entered here will be discarded. Avatar Creations will not be consumed.',
          confirm: 'Discard and upload again',
          escape: 'Discard and re-upload',
        },
        reject: {
          reuploadButton: 'Upload another photo',
          reason: {
            unreadable_image: 'Couldn\'t read this image',
            low_resolution_pre: 'Image resolution is too low',
            low_resolution: 'Image resolution is too low',
            file_too_large: 'Image is too large',
            unsupported_format: 'Image format not supported',
            face_too_small: 'Face is too small',
            no_face: 'No face detected',
            multiple_faces: 'More than one face detected',
            multiple_humans_llm: 'More than one person in frame',
            non_human_subject: 'Subject is not a human',
            no_human_subject: 'No human subject in this image',
            minor_child: 'Subject appears to be a minor',
            infant_toddler: 'Subject looks like a baby or toddler',
            extreme_lighting: 'Lighting is too harsh',
            occlusion: 'Face is partially obstructed',
            content_sexual: 'This photo contains revealing or adult content',
            content_minor: 'This photo may raise child-safety concerns',
            content_violence: 'This photo contains violent, graphic, or dangerous content',
            content_other: 'This photo may contain inappropriate content',
            unknown: 'This photo could not be accepted',
          },
          recovery: {
            unreadable_image: 'Try a different image file.',
            low_resolution_pre: 'Use a photo with at least 340px on the short side.',
            low_resolution: 'Use a photo with at least 340px on the short side.',
            file_too_large: 'Choose an image smaller than 10 MB.',
            unsupported_format: 'Use a JPG or non-transparent PNG file.',
            face_too_small: 'Move closer to the camera or use a higher-res photo.',
            no_face: 'Use a clear front-facing portrait.',
            multiple_faces: 'Use a photo with only one person.',
            multiple_humans_llm: 'Use a single-person image.',
            non_human_subject: 'Avatars require a real human portrait.',
            no_human_subject: 'Try a portrait photo of a person.',
            minor_child: 'Avatars must be of an adult.',
            infant_toddler: 'Use an adult, or a cartoon character with near-human proportions.',
            extreme_lighting: 'Try more even, softer lighting.',
            occlusion: 'Remove glasses, masks, or anything covering the face.',
            content_sexual: 'Please use a photo in everyday clothing.',
            content_minor: 'Please use a photo of an adult in everyday clothing.',
            content_violence: 'Please use an everyday portrait photo.',
            content_other: 'Our AI can\'t process this photo. Please use a front-facing photo in everyday clothing.',
            unknown: 'Try another clear, front-facing portrait.',
          },
        },
      },
    },
    library: {
      subtitle: 'Browse and explore public avatar library',
    },
  },
  onboardingSurvey: {
    title: 'Tell us a bit about yourself',
    description: 'Every question is optional. Answer whichever you like; it helps us shape Studio around what you are building.',
    optional: 'Optional',
    otherPlaceholder: 'Tell us more',
    answered: '{count} of {total} answered',
    skip: 'Skip',
    submit: 'Submit',
    progress: 'Question {current} of {total}',
    chooseOne: 'Choose one option. You can change your answer.',
    chooseMany: 'Choose all that apply.',
    optionalNote: 'Every question is optional.',
    continue: 'Continue',
    skipQuestion: 'Skip question',
    back: 'Back',
    submitEarly: 'Submit now',
    closeSurvey: 'Close survey',
    close: 'Close survey',
    otherLabel: 'Tell us more (optional)',
    thankYouTitle: 'Thanks for your feedback!',
    thankYouDescription: 'Your answers help us keep improving Studio.',
    startUsing: 'Start using Studio',
    autoClose: 'Closing automatically in {seconds}s',
  },
  page: {
    avatarPagination: {
      label: 'Avatar pagination',
      previous: 'Previous',
      next: 'Next',
      position: 'Page {page} of {total}',
      currentPage: 'Page {page}',
      goToPage: 'Go to page {page}',
    },
    library: libraryPage.enUS,
    custom: customPage.enUS,
    apps: appsPage.enUS,
    usage: usagePage.enUS,
    billing: billingPage.enUS,
    account: accountPage.enUS,
    auth: authPage.enUS,
    integrate: integratePage.enUS,
    utility: utilityPage.enUS,
    notifications: notificationsPage.enUS,
    support: supportPage.enUS,
  },
  shell: {
    breadcrumbs: 'Breadcrumb',
    appDetails: 'App details',
    avatarDetails: 'Avatar details',
    studio: 'Studio',
    collapse: 'Collapse sidebar',
    expand: 'Expand sidebar',
    openNavigation: 'Open navigation',
    closeNavigation: 'Close navigation',
    mainNavigation: 'Main navigation',
    modules: 'Studio modules',
    creditsRemaining: 'Credits remaining',
    creditsOf: '{value} of {total} credits remaining',
    creditsAvailable: '{value} credits remaining',
    creditsUnavailable: 'Balance unavailable',
    creditsLoading: 'Loading balance…',
    runningLow: 'Credits running low',
    noneLeft: 'No credits remaining',
    managePlan: 'Manage Plan',
    viewCredits: 'View credit usage',
    accountMenu: 'Open account menu',
    signOut: 'Sign out',
  },
  dashboard: {
    accountOverview: 'Plan and support',
    tickets: { title: 'Open tickets', list: 'Open ticket list', open: 'Open ticket: {title}', unread: 'Unread update' },
    welcome: 'Welcome to Studio',
    welcomeBack: 'Welcome back, {name}',
    period: '{range} · Last 7 days · UTC',
    loadFailed: 'Workspace unavailable',
    loadFailedHint: 'Your avatars and activity will appear when the data is available',
    gallery: {
      title: 'Try an avatar',
      browse: 'Browse avatars',
      previous: 'Previous avatars',
      next: 'Next avatars',
      group: 'Public avatars',
      action: 'Try avatar',
    },
    integration: {
      title: 'Build with an avatar',
      continueTitle: 'Continue your integration',
      description: 'Add an avatar to your app in four steps with our SDK',
      action: 'Set up integration',
      continueAction: 'Continue setup',
      stepsLabel: 'Integration steps',
      steps: {
        choose: { title: 'Choose an avatar', hint: 'Pick a public avatar or one you created' },
        key: { title: 'Get an API key', hint: 'Get your app ID and first API key' },
        build: { title: 'Start building', hint: 'Run the demo or add the SDK for Web, iOS and Android' },
        verify: { title: 'Verify', hint: 'See your first sessions arrive in Usage' },
      },
    },
    metrics: {
      open: 'Open {metric} in Usage',
      connectionTime: 'Connection time',
      connectionTimeHint: 'Across all apps',
      sessions: 'Sessions',
      sessionsHint: 'Total in this period',
      peak: 'Peak concurrent',
      peakHint: 'Connections at one time',
      live: 'Live connections',
      liveHint: 'Connected now',
      slots: '/ {count} slots',
    },
    chart: {
      title: 'Connection activity',
      caption: 'Daily connection time · All apps',
      unit: 'min',
      totalLabel: 'Total in period',
      empty: 'No data for this period',
      emptyHint: 'Start a session to see your usage here',
      open: 'Open connection activity in Usage',
    },
    balance: {
      label: 'Available balance',
      credits: 'Usage credits',
      creditsUnit: 'credits',
      creations: 'Avatar creations',
      creationsUnit: 'creations',
      runningLow: 'Running low',
      noneLeft: 'None left',
      unavailable: 'Unavailable',
      remaining: '{value} of {total} {unit} remaining',
    },
    recent: {
      title: 'Recently used',
      hint: 'From your sessions · Last 90 days',
      tryTitle: 'Try an avatar',
      tryHint: 'Start a conversation with a public avatar',
      publicAvatar: 'Public avatar',
      customAvatar: 'Custom avatar',
      open: 'Open avatar',
      lastConnected: 'Last connected {time} UTC',
      create: 'Create Avatar',
    },
    copy: {
      copyId: 'Copy {name} avatar ID',
      copiedId: 'Copied {name} avatar ID',
      copied: 'Copied',
    },
  },
  stats: {
    title: 'Usage Statistics',
    subtitle: 'View API usage, session history, and Avatar Creations.',
    tabs: {
      overview: 'Overview',
      sessionHistory: 'Session History',
      avatarCreations: 'Avatar Creations',
    },
    timeRange: {
      'today': 'Today',
      '7d': '7 Days',
      '14d': '14 Days',
      '30d': '30 Days',
      '90d': '90 Days',
      '1y': '1 Year',
    },
    usage: {
      title: 'Connection Usage',
      total: 'Total Duration',
      chart: 'Connection Duration Trend',
      unit: {
        hours: 'hours',
        minutes: 'min',
        seconds: 'sec',
      },
    },
    session: {
      title: 'Connections',
      total: 'Total Connections',
      chart: 'Connection Count Trend',
      unit: 'connections',
      maxConcurrent: 'Peak Concurrent Connections',
    },
    realtime: {
      title: 'Live Connections',
    },
    noData: 'No data available',
    noDataHint: 'Start a session to see your usage data here',
    noDataCreateKey: 'Create API Key',
    noDataViewDocs: 'View Documentation',
    loading: 'Loading...',
    fetchFailed: 'Failed to fetch statistics',
  },
  connectionLogs: {
    title: 'Connection Logs',
    loading: 'Loading...',
    noData: 'No connection logs found',
    fetchFailed: 'Failed to fetch connection logs',
    filter: {
      allApps: 'All Apps',
      deleted: 'deleted',
    },
    table: {
      connectedAt: 'Connected At',
      disconnectedAt: 'Disconnected At',
      duration: 'Duration',
      appId: 'App ID',
      avatarId: 'Avatar ID',
      region: 'Region',
      active: 'Active',
    },
    pagination: {
      showing: 'Showing {from}-{to} of {total}',
      prev: 'Previous',
      next: 'Next',
    },
  },
  feedback: {
    myTickets: 'My Tickets',
    submitFeedback: 'Submit Feedback',
    submitDescription: 'Submit feedback and issues, we will process them promptly',
    ticketDetail: 'Ticket Details',
    noTickets: 'No tickets yet',
    noTicketsDescription: 'Submit feedback and we will get back to you',
    category: 'Category',
    title: 'Title',
    description: 'Description',
    platforms: 'Platforms',
    integrationModes: 'Integration Mode',
    status: 'Status',
    createdAt: 'Created',
    reply: 'Reply',
    sendReply: 'Send Reply',
    closeTicket: 'Close Ticket',
    closeConfirm: 'Close this ticket? You won\'t be able to reply after closing.',
    ticketClosed: 'This ticket is closed',
    toastCreateSuccess: 'Ticket created successfully',
    toastUpdateSuccess: 'Ticket updated successfully',
    toastCloseSuccess: 'Ticket closed',
    toastReplySuccess: 'Reply sent successfully',
    toastCreateFailed: 'Failed to create ticket',
    toastListFailed: 'Failed to load tickets',
    toastDetailFailed: 'Failed to load ticket details',
    toastUpdateFailed: 'Failed to update ticket',
    toastCloseFailed: 'Failed to close ticket',
    toastReplyFailed: 'Failed to send reply',
    replyPlaceholder: 'Type your reply...',
    titlePlaceholder: 'Briefly describe your issue',
    descriptionPlaceholder: 'Describe the issue in detail, including steps to reproduce, expected behavior, etc.',
    submit: 'Submit',
    cancel: 'Cancel',
    back: 'Back',
    admin: 'Support',
    me: 'Me',
    noReplies: 'No replies yet',
    noRepliesDescription: 'We will process your feedback shortly',
    statusPending: 'Pending',
    statusInProgress: 'In Progress',
    statusReplied: 'Replied',
    statusClosed: 'Closed',
    categorySdkIntegration: 'SDK Integration',
    categoryApiError: 'API Error',
    categoryDocsUnclear: 'Documentation',
    categoryBilling: 'Billing',
    categoryAvatar: 'Avatar',
    categoryOther: 'Other',
    platformWeb: 'Web',
    platformIos: 'iOS',
    platformAndroid: 'Android',
    platformFlutter: 'Flutter',
    integrationDirectMode: 'Direct Mode',
    integrationBackendMode: 'Backend Mode',
    integrationLiveKitAgents: 'LiveKit Agents Integration',
    integrationAgoraConvo: 'Agora Convo AI / TEN Framework Integration Beta',
    integrationBackendModeWithLiveKit: 'Backend Mode with LiveKit transport',
    needHelp: 'Need help?',
    needHelpDescription: 'Submit a feedback ticket, we\'ll reply soon',
    newTicket: 'New Ticket',
    viewMyTickets: 'View My Tickets',
    statusFilter: 'Status Filter',
    all: 'All',
    categoryRequired: 'Please select a category',
    titleRequired: 'Please enter a title',
    descriptionRequired: 'Please enter a description',
    ticketNotFound: 'Ticket not found',
    backToList: 'Back to List',
    replyHistory: 'Reply History',
    send: 'Send',
    confirmClose: 'Confirm Close',
    welcomeTitle0: 'Hey, I\'m here',
    welcomeTitle1: 'Got something to say?',
    welcomeTitle2: 'Need backup?',
    welcomeTitle3: 'Hit a snag?',
    welcomeMessage0: 'Stuck on something? Drop us a ticket and we\'ll sort it out',
    welcomeMessage1: 'Bug or suggestion — we want to hear it all',
    welcomeMessage2: 'Engineers standing by, don\'t be shy',
    welcomeMessage3: 'Any questions? Drop a ticket and we\'ll get back to you ASAP',
    integrationHint0: 'Hit a wall? Drop a ticket, our engineers are standing by',
    integrationHint1: 'API errors? SDK issues? Submit a ticket and we\'ll investigate',
    integrationHint2: 'Docs unclear? Don\'t struggle alone, ask us',
    integrationHint3: 'Integration problems? Our engineers are ready to help',
    recentTickets: 'Ticket Updates',
    viewAll: 'View All',
    noTicketsYet: 'All clear',
    noTicketsHint: 'No issues so far? Great! Reach out anytime if you need us',
    gotIt: 'Got it!',
  },
  pricing: {
    subtitle: 'Choose the pricing plan that fits your needs',
    title: 'Pricing Plans',
    description: 'Select the right plan for your usage',
    monthlyPlan: 'Monthly Plan',
    annualPlan: 'Annual Plan',
    annualBillingNote: 'billed {price}/year',
    save20: 'SAVE 20%',
    contactUs: 'Contact Us',
    usageDisclaimer: 'Usage Disclaimer: Plans include monthly usage limits. If you exceed them, please contact us at',
    comingSoon: 'Payment feature coming soon',
    alreadySubscribed: 'You are already subscribed to this plan or a higher plan.',
    payment: {
      paymentSuccessful: 'Payment Successful! Your subscription is now active.',
      paymentCanceled: 'Payment was canceled. You can try again anytime.',
      failedToCreatePayment: 'Failed to create subscription. Please try again.',
      downgradeScheduled: 'Plan change scheduled. It will take effect on {date}.',
      downgradeScheduledGeneric: 'Plan change scheduled. It will take effect at the end of your current billing period.',
      cancelScheduled: 'Your subscription will be canceled at the end of the current billing period.',
    },
    downgrade: {
      confirmTitle: 'Confirm Downgrade',
      confirmDescription: 'Are you sure you want to downgrade to {plan}? The change will take effect at the end of your current billing period.',
      confirm: 'Confirm Downgrade',
    },
    comparison: {
      title: 'Compare Plans',
      monthlyCredits: 'Monthly Credits',
      perMinuteRate: 'Per Minute Rate',
      concurrentSessions: 'Max Concurrent Sessions',
      sessionDuration: 'Max Session Duration',
      avatarUnits: 'Avatar Creations',
      avatarUnitsValue: '{first} first month · {monthly}/mo renewal',
      avatarUnitsValueSame: '{count}/mo',
      avatarUnitsValueOneTime: '{count} one-time',
      avatarUnitsNone: '—',
      unlimited: 'Unlimited',
    },
    cardFeatures: {
      avatarUnitsSplit: '{first} Avatar Creations first month · {monthly}/mo renewal',
      avatarUnitsSame: '{count} Avatar Creations / month',
      avatarUnitsOneTime: '{count} Avatar Creations one-time',
    },
    avatarCreationsAlternative: {
      title: 'Only need Avatar Creations?',
      description: 'Buy a one-time package without changing your plan. Purchased Avatar Creations never expire.',
      action: 'Buy Avatar Creations',
    },
  },
  notifications: {
    eyebrow: 'Inbox',
    title: 'Notifications',
    summary: '{count} unread notifications',
    allCaughtUp: 'You are all caught up.',
    unreadCount: '{count} unread',
    resultCount: '{count} notifications',
    viewAll: 'View all',
    markAllRead: 'Mark all as read',
    loadMore: 'Load more',
    loadFailed: 'Notifications could not be loaded',
    retryHint: 'Check your connection and try again.',
    emptyRecent: 'Nothing new right now',
    bell: {
      label: 'Notifications',
      unreadLabel: 'Notifications, {count} unread',
      subtitle: 'Account and activity updates',
    },
    filters: {
      label: 'Notification filters',
      all: 'All',
      unread: 'Unread',
    },
    empty: {
      all: { title: 'No notifications yet', body: 'Important account and activity updates will appear here.' },
      unread: { title: 'No unread notifications', body: 'You have read everything in your inbox.' },
    },
    events: {
      welcome: { title: 'Welcome to Spatius Studio', body: 'Your account is ready. Start building something vivid.' },
      avatarReady: { title: 'Avatar ready', body: '{avatarName} has finished processing and is ready to use.' },
      avatarFailed: { title: 'Avatar creation failed', body: 'We could not finish {avatarName}. Your Avatar Creation has been returned.' },
      feedbackReceived: { title: 'Feedback received', body: 'We received {ticketNo}: {title}' },
      feedbackReply: { title: 'New support reply', body: '{ticketNo}: {replyPreview}' },
      autoReloadSucceeded: { title: 'Credits reloaded', body: '{creditsAdded} credits were added. Your new balance is {newBalance}.' },
      autoReloadDisabled: { title: 'Auto-reload disabled', body: 'Repeated payment failures disabled auto-reload. Review your billing settings.' },
      lowBalance: { title: 'Credit balance is low', body: 'Your balance is {currentBalance} credits, below the {threshold} credit threshold.' },
      unknown: { title: 'Account update', body: 'There is a new update for your Spatius Studio account.' },
    },
  },
  avatarUnits: {
    sectionTitle: 'Avatar Creations',
    sectionSubtitle: 'Creating one avatar uses one Avatar Creation. Purchased Avatar Creations never expire.',
    policyNote: 'Deducted when you submit. Returned automatically if creation fails. Not returned when you delete an avatar that was created successfully.',
    usageDescription: 'View your balance, expiry dates, and transaction history in Usage.',
    balance: '{count} Avatar Creations available',
    balanceUnavailable: 'Balance unavailable',
    expiringSoon: '{count} expiring soon',
    viewUsage: 'View Usage',
    viewPlans: 'View Plans',
    purchasing: 'Redirecting to checkout...',
    package: {
      units: '{count} Avatar Creations',
      perUnit: '~{price}/creation',
    },
    validity: {
      permanent: 'Never expires',
      expiresAt: 'Expires {date}',
      expiringSoon: 'Expiring soon',
      expired: 'Expired',
      valid: 'Valid',
    },
    source: {
      register: 'Signup bonus',
      subscriptionFirst: 'First subscription',
      subscriptionRenewal: 'Subscription renewal',
      subscriptionUpgrade: 'Subscription upgrade',
      purchase: 'Purchase',
      manualGrant: 'Admin grant',
      refund: 'Failure refund',
    },
    changeType: {
      grant: 'Granted',
      consume: 'Consumed',
      expire: 'Expired',
      refund: 'Refunded',
    },
    checkout: {
      canceled: 'Checkout was canceled. You can choose a package and try again.',
      processing: 'Payment succeeded. Updating your Avatar Creations balance...',
      confirmed: 'Avatar Creations added to your account.',
      delayed: 'We could not confirm the balance update yet. Refresh later; if it still has not arrived, contact support.',
      startFailed: 'Unable to start checkout. Please try again.',
    },
    createGate: {
      loadingTitle: 'Checking Avatar Creations',
      loadingDescription: 'We are confirming your available Avatar Creations.',
      errorTitle: 'Unable to check Avatar Creations',
      errorDescription: 'We could not confirm your balance. Please retry before uploading a photo.',
      emptyTitle: 'No Avatar Creations available',
      emptyDescription: 'You need {cost} Avatar Creation to create an avatar. Plans include recurring Avatar Creations so you can keep creating.',
      pendingTitle: 'Updating your Avatar Creations',
      pendingDescription: 'Payment was received and your balance is being updated. This can take a moment.',
      availableLine: 'Available: {available} Avatar Creations · This will use {cost} Avatar Creation when you create.',
      finalUseLine: 'Create uses {cost} Avatar Creation · {available} available',
      finalBalanceLine: '{available} Avatar Creations available',
      refreshBalance: 'Refresh balance',
      contactForFreeUnits: 'Contact us for free Avatar Creations',
      contactSubject: 'Avatar Creations trial request',
      contactPrefix: 'Need trial support, enterprise purchase, or special help? Contact ',
      contactSuffix: '.',
      concurrentLimit: 'You already have an avatar creation in progress. Please try again later.',
      unknownResultTitle: 'Creation status needs a check',
      unknownResultDescription: 'The creation request may have reached the server. Check Custom Avatars before retrying so you do not submit the same avatar twice.',
      viewPersonal: 'View Custom Avatars',
    },
    historyPage: {
      title: 'Avatar Creations',
      description: 'Track your remaining Avatar Creations, expiration dates, and activity.',
      tabBatches: 'Expiry Details',
      tabTransactions: 'Transaction History',
      availableUnitsLabel: 'available Avatar Creations',
      emptyGrants: 'No Avatar Creations available.',
      emptyTransactions: 'No activity yet.',
      remaining: '{count} of {total}',
      unitsCol: 'Creations',
      sourceCol: 'Source',
      validityCol: 'Validity',
      timeCol: 'Time',
      changeCol: 'Change',
      balanceAfterCol: 'Balance after',
      backToLibrary: 'Back to avatars',
      balanceLoadFailed: 'Unable to load your Avatar Creations balance.',
      loadMore: 'Load more',
    },
    buyPage: {
      title: 'Buy Avatar Creations',
      description: 'Choose a package to create more avatars. Purchased Avatar Creations never expire and can be used by Free, Starter, and Scale users.',
      currentBalance: 'Current balance',
      units: 'creations',
      purchaseNote: 'Avatar Creations are only consumed when an avatar creation is submitted successfully.',
      emptyPackages: 'No packages are available yet.',
      popular: 'Popular',
      selectHint: 'Select a package to continue.',
      selected: '{count} creations selected · {price}',
      checkout: 'Continue to checkout',
      createAvatar: 'Create an Avatar',
      backToUsage: 'Back to Usage',
    },
  },
}
